import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";
import { TONES } from "@/components/common";
import { todayIso, parseIso, toIso } from "@/lib/dates";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const DRAG_THRESHOLD = 8;

export const DayStrip = ({ days, selected, onSelect, tone, isDone, isSkipped, showPicker = true }) => {
  const stripRef = useRef(null);
  const chipRefs = useRef([]);
  const drag = useRef({
    tracking: false,
    dragging: false,
    startX: 0,
    scrollLeft: 0,
    pointerId: null,
  });
  const ignoreClickRef = useRef(false);
  const today = todayIso();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragging, setDragging] = useState(false);

  const dateSet = useMemo(() => new Set((days || []).map((d) => d.date)), [days]);
  const selectedDate = days?.[selected]?.date || null;
  const selectedAsDate = selectedDate ? parseIso(selectedDate) : undefined;

  useEffect(() => {
    if (selected == null) return;
    if (drag.current.dragging) return;
    const id = requestAnimationFrame(() => {
      const chip = chipRefs.current[selected];
      const strip = stripRef.current;
      if (!chip || !strip) return;
      const target = chip.offsetLeft - strip.clientWidth / 2 + chip.offsetWidth / 2;
      strip.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [selected, days.length]);

  const jumpToIso = (iso) => {
    if (!iso) return;
    const idx = days.findIndex((d) => d.date === iso);
    if (idx >= 0) onSelect(idx);
  };

  const onPick = (date) => {
    const iso = toIso(date);
    if (!iso || !dateSet.has(iso)) return;
    jumpToIso(iso);
    setPickerOpen(false);
  };

  const onPointerDown = (e) => {
    const strip = stripRef.current;
    // Solo arrastre con ratón/stylus; el touch nativo ya hace scroll horizontal
    if (!strip || e.pointerType === "touch" || e.button === 2) return;
    drag.current = {
      tracking: true,
      dragging: false,
      startX: e.clientX,
      scrollLeft: strip.scrollLeft,
      pointerId: e.pointerId,
    };
  };

  const onPointerMove = (e) => {
    const strip = stripRef.current;
    const d = drag.current;
    if (!strip || !d.tracking) return;
    const dx = e.clientX - d.startX;
    if (!d.dragging && Math.abs(dx) >= DRAG_THRESHOLD) {
      d.dragging = true;
      setDragging(true);
      try { strip.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }
    if (d.dragging) {
      strip.scrollLeft = d.scrollLeft - dx;
      e.preventDefault();
    }
  };

  const endDrag = () => {
    const strip = stripRef.current;
    const d = drag.current;
    if (!d.tracking) return;
    const wasDragging = d.dragging;
    if (wasDragging && strip && d.pointerId != null) {
      try { strip.releasePointerCapture(d.pointerId); } catch { /* ignore */ }
      // El navegador puede emitir un click fantasma tras soltar el arrastre
      ignoreClickRef.current = true;
      setTimeout(() => { ignoreClickRef.current = false; }, 80);
    }
    drag.current = { tracking: false, dragging: false, startX: 0, scrollLeft: 0, pointerId: null };
    setDragging(false);
  };

  const onChipClick = (i, e) => {
    if (ignoreClickRef.current || drag.current.dragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onSelect(i);
  };

  return (
    <div className="space-y-2" data-testid="day-strip-wrap">
      {showPicker && (
        <div className="flex flex-wrap items-center gap-2 px-4 sm:px-0">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="day-picker-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:border-stone-400"
              >
                <CalendarDays className="h-3.5 w-3.5" />
                {selectedDate || "Elegir día"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto rounded-2xl p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedAsDate}
                onSelect={onPick}
                defaultMonth={selectedAsDate}
                disabled={(date) => {
                  const iso = toIso(date);
                  return !iso || !dateSet.has(iso);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {selectedDate !== today && dateSet.has(today) && (
            <button
              type="button"
              data-testid="day-strip-today-btn"
              onClick={() => jumpToIso(today)}
              className="rounded-full px-3 py-1.5 text-xs font-semibold text-stone-600 hover:bg-stone-100 hover:text-ink"
            >
              Ir a hoy
            </button>
          )}
        </div>
      )}
      <div
        ref={stripRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={(e) => {
          // Si salimos del strip sin captura, cancelar tracking limpio
          if (drag.current.tracking && !drag.current.dragging) endDrag();
        }}
        className={`no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-2 touch-pan-x sm:mx-0 sm:px-0 ${dragging ? "cursor-grabbing select-none" : "cursor-grab"}`}
        data-testid="day-strip"
      >
        {days.map((d, i) => {
          const active = i === selected;
          const isToday = d.date === today;
          const rest = d.workout?.kind === "descanso";
          const skipped = isSkipped?.(d);
          const done = isDone?.(d);
          let dot = "bg-transparent";
          if (done) dot = active ? "bg-white" : TONES[tone].dot;
          else if (skipped) dot = active ? "bg-white/70" : "bg-stone-400";
          else if (rest) dot = "bg-stone-200";
          return (
            <button
              key={d.date}
              type="button"
              ref={(el) => { chipRefs.current[i] = el; }}
              onClick={(e) => onChipClick(i, e)}
              data-testid={`day-chip-${i}`}
              aria-current={isToday ? "date" : undefined}
              title={d.date}
              className={`flex min-w-[58px] shrink-0 flex-col items-center rounded-2xl border px-2 py-2.5 transition-colors active:scale-95 ${active ? `${TONES[tone].solid} border-transparent` : isToday ? "border-ink bg-stone-50 ring-2 ring-ink/15" : "border-stone-200 bg-white hover:border-stone-400"}`}
            >
              <span className={`text-[11px] font-semibold ${active ? "text-white/80" : "text-stone-500"}`}>{isToday ? "Hoy" : d.weekday.slice(0, 3)}</span>
              <span className="font-display text-lg font-extrabold">{d.date.slice(8)}</span>
              <span className={`mt-0.5 h-1.5 w-1.5 rounded-full ${dot}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};
